import { ExternalLink, Info, Eye, Heart, CheckCircle, Clock } from "lucide-react";
import { formatDateUS } from "@/api";
import { trackLinkClick } from "@/lib/track";
import SocialLinksSection from "@/components/shared/SocialLinksSection";
import { API, axios } from "@/api";
import { DetailRow } from "./offerUtils";
import { useOfferDetail } from "./OfferDetailContext";

export default function OfferConfidenceCard() {
  const { offer, businessId, confidenceColor, funnel, navigate } = useOfferDetail();

  return (
    <>
      {/* Social Media Links */}
      <SocialLinksSection
        socialMedia={offer.social_media}
        businessId={businessId}
        businessName={offer.name}
        onLinkAdded={() => {
          axios.get(`${API}/offers/${businessId}`).then(r => {
            if (r.data?.social_media) offer.social_media = r.data.social_media;
            window.location.reload();
          });
        }}
      />

      {/* Confidence & Source Card */}
      <div data-detail-card className={`${confidenceColor}`} data-testid="confidence-card">
        <h3 className="font-semibold mb-2 flex items-center gap-2"><Info className="w-5 h-5" />Confidence: {offer.confidence_label} ({offer.confidence_score}%)</h3>
        <div className="divide-y divide-current/10">
          <DetailRow label="Source" value={offer.source_label || "Unknown"} />
          {offer.crowd_verification_count > 0 ? (
            <>
              <div className="flex items-center justify-between py-2.5 border-b border-current/10">
                <span className="text-sm text-slate-500 flex items-center gap-1.5">Community</span>
                <button
                  onClick={() => navigate(`/offer/${businessId}/verifications`)}
                  className="text-sm font-semibold text-emerald-600 hover:text-emerald-700 underline underline-offset-2 flex items-center gap-1"
                  data-testid="view-verifications-link"
                >
                  {offer.crowd_verification_count} verified
                  <ExternalLink className="w-3 h-3" />
                </button>
              </div>
              <DetailRow label="Last Verified" value={offer.crowd_last_verified ? formatDateUS(offer.crowd_last_verified) : "—"} />
            </>
          ) : (
            <DetailRow label="Community" value="Not yet verified" valueColor="text-slate-400" />
          )}
          <DetailRow label="Data Updated" value={formatDateUS(offer.data_updated)} />
          {offer.staleness_level && offer.staleness_level !== "unknown" && (
            <div className="flex items-center justify-between py-2.5">
              <span className="text-sm text-slate-500 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />Verification Age</span>
              <span className={`text-sm font-medium flex items-center gap-1 ${
                offer.staleness_level === "fresh" ? "text-green-600" :
                offer.staleness_level === "recent" ? "text-amber-600" : "text-red-500"
              }`}>
                <span className={`w-2 h-2 rounded-full inline-block ${
                  offer.staleness_level === "fresh" ? "bg-green-400" :
                  offer.staleness_level === "recent" ? "bg-amber-400" : "bg-red-400"
                }`} />
                {offer.days_since_verified != null ? `${offer.days_since_verified}d ago` : "—"}
                {offer.staleness_level === "stale" && " — call ahead"}
              </span>
            </div>
          )}
          {offer.freshness_cta && (
            <div className="flex items-center justify-between py-2.5">
              <span className="text-sm text-slate-500 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />Freshness</span>
              <span className={`text-sm font-medium ${offer.freshness_status === "stale" ? "text-amber-600" : offer.freshness_status === "old" ? "text-red-500" : "text-sky-600"}`}>{offer.freshness_cta}</span>
            </div>
          )}
        </div>
        {/* Social proof */}
        {funnel && funnel.views > 0 && (
          <div className="mt-3 pt-3 border-t border-current/10 flex items-center gap-4 text-xs text-slate-500" data-testid="social-proof-inline">
            <span className="flex items-center gap-1"><Eye className="w-3 h-3 text-sky-500" /><strong className="text-slate-700">{funnel.views}</strong> viewed</span>
            {funnel.favorites > 0 && <span className="flex items-center gap-1"><Heart className="w-3 h-3 text-pink-500" /><strong className="text-slate-700">{funnel.favorites}</strong> saved</span>}
            {funnel.claimed > 0 && <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-emerald-500" /><strong className="text-slate-700">{funnel.claimed}</strong> claimed</span>}
          </div>
        )}
        {offer.verification_notes && <p className="text-xs mt-2 opacity-70">{offer.verification_notes}</p>}
        {(offer.source_url || offer.offer_source_url || offer.verification_url) && (
          <a
            href={offer.source_url || offer.offer_source_url || offer.verification_url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackLinkClick("source_url", offer.source_url || offer.offer_source_url || offer.verification_url)}
            className="mt-3 pt-3 border-t border-current/10 flex items-center gap-1.5 text-xs text-slate-400 hover:text-sky-500 transition-colors"
            data-testid="verify-source-link"
          >
            <ExternalLink className="w-3 h-3" />Verify this offer
          </a>
        )}
      </div>

      {/* Location Notes */}
      {offer.location_notes && (
        <div data-detail-card="white"><h3 className="font-semibold text-slate-800 mb-2">Location Notes</h3><p className="text-sm text-slate-600">{offer.location_notes}</p></div>
      )}
    </>
  );
}
